import { useEffect, useState } from "react"
import { Meeting, TaskStatus } from "@/schema"
import { useQueryClient } from "@tanstack/react-query"
import { Button, ScrollArea } from "@ui/shad"
import { Loader2, Plus } from "lucide-react"

import { isMeetingPromptFinished } from "@/lib/prompt-status"
import { useMeetingTasks } from "@/hooks/api/meetings/queries"
import { useScrollAreaHeight } from "@/hooks/use-scroll-area-height"
import MeetingTasksListSkeleton from "@/components/meetings/recap/meeting-tasks-list-skeleton"
import { TaskCard } from "@/components/tasks/card"
import { useCreateTaskComponent } from "@/components/tasks/use-create-task-component"

export default function MeetingTasksList({
  meeting,
  isVideoCollapsed,
  handleSeekTo,
  isGeneratingTasks,
}: {
  meeting?: Meeting
  isVideoCollapsed: boolean
  handleSeekTo?: (time: number) => void
  isGeneratingTasks?: boolean
}) {
  const { tableTop, containerRef } = useScrollAreaHeight()
  const queryClient = useQueryClient()
  const createTask = useCreateTaskComponent({
    defaultHousehold: meeting?.households?.[0],
  })

  const [awaitingTasks, setAwaitingTasks] = useState(false)

  useEffect(() => {
    if (!isGeneratingTasks) return
    setAwaitingTasks(true)
    const timeout = setTimeout(() => setAwaitingTasks(false), 60_000)
    return () => clearTimeout(timeout)
  }, [isGeneratingTasks])

  const { data: tasksData, isLoading: isLoadingTasks } = useMeetingTasks(meeting?.id ?? "", {
    enabled: !!meeting?.id,
    refetchInterval: (query) => {
      if (!awaitingTasks) return false
      const currentTasks = query.state.data?.tasks ?? []
      return currentTasks.length === 0 ? 3000 : false
    },
  })
  const tasks = tasksData?.tasks ?? []

  useEffect(() => {
    if (awaitingTasks && tasks.length > 0) setAwaitingTasks(false)
  }, [awaitingTasks, tasks.length])

  // Sort tasks: todo -> pending -> done -> rejected
  const STATUS_ORDER: Record<string, number> = {
    [TaskStatus.TODO]: 0,
    [TaskStatus.PENDING]: 1,
    [TaskStatus.DONE]: 2,
    [TaskStatus.REJECTED]: 3,
  }
  const sortedTasks = [...tasks].sort((a, b) => {
    const aStatus = a.optimistic_status === TaskStatus.DONE ? TaskStatus.DONE : a.status
    const bStatus = b.optimistic_status === TaskStatus.DONE ? TaskStatus.DONE : b.status
    return (STATUS_ORDER[aStatus] ?? 1) - (STATUS_ORDER[bStatus] ?? 1)
  })
  const showNoTasksFoundMessage =
    !awaitingTasks && sortedTasks.length === 0 && isMeetingPromptFinished(meeting, "Prompt::MeetingTasks")

  useEffect(() => {
    if (containerRef.current) {
      window.dispatchEvent(new Event("resize"))
    }
  }, [containerRef, isVideoCollapsed, isLoadingTasks])

  if (isLoadingTasks) {
    return <MeetingTasksListSkeleton />
  }

  return (
    <div ref={containerRef} className="flex-1">
      <ScrollArea style={{ height: `calc(100vh - ${tableTop}px)` }}>
        <div className="flex flex-col">
          {awaitingTasks && sortedTasks.length === 0 && (
            <div className="flex items-center justify-center gap-2 px-4 pt-6 pb-4 text-sm">
              <Loader2 className="size-4 animate-spin" />
              <span className="text-muted-foreground">Generating tasks...</span>
            </div>
          )}
          {showNoTasksFoundMessage && (
            <div className="flex items-center justify-center px-4 pt-6 pb-4 text-sm">
              <span className="text-muted-foreground">No tasks found for this meeting</span>
            </div>
          )}
          {sortedTasks.map((task, index) => (
            <TaskCard
              key={task.id}
              task={task}
              size="sm"
              hideHousehold
              hidePriority
              hideLabel
              handleSeekTo={handleSeekTo}
              allowDelete
              allowReject
              taskList={sortedTasks}
              taskIndex={index}
            />
          ))}
          {meeting && (
            <div className="px-4 py-3">
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => {
                  createTask.open({
                    recommendable: {
                      type: "Meeting",
                      id: meeting.id,
                    },
                    onCreated: () => {
                      queryClient.invalidateQueries({ queryKey: ["meeting", meeting.id, "tasks"] })
                    },
                  })
                }}
              >
                <Plus />
                Create task
              </Button>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  )
}
